import * as React from 'react';

import './metadataFormField.scss';
import { Metadata, Schema, SchemaField } from '../../types/Metadata';
import { FieldType } from '../../enums/FieldType';
import { DataType } from '../../enums/DataType';
import { ReferenceField } from './ReferenceField/referenceField';
import { AdditionalMandatoryReviewerField } from './AdditionalMandatoryReviewersField/additionalMandatoryReviewers';
import { NumberField } from './NumberField/numberField';
import { TextField } from './TextField/textField';
import { DateField } from './DateField/dateField';
import { BooleanField } from './BooleanField/booleanField';
import { TreeField } from './TreeField/treeField';
import { TreeFieldUtils } from '../../utils/treeFieldUtils';
import AccessControl from '../AccessControl/accessControl';
import { TextAreaField } from './TextAreaField/textAreaField';
import { RelationshipField } from './RelationshipField/relationshipField';
import { LabelField } from './Label/label';
import { FieldShowValue } from '../../enums/FieldShowValue';
import { DataSourceField } from './DataSourceField/dataSourceField';
import { EnumField } from './EnumField/enumField';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { ConferenceInformationField } from './ConferenceInformationField/conferenceInformationField';
import { ConfigureWorkflowField } from './ConfigureWorkflowField/configureWorkflowField';
import { ComplimentaryResearchField } from './ComplimentaryResearchField/complimentaryResearchField';
import { DelegateField } from './DelegateField/delegateField';
import { CoAuthorsField } from './CoAuthorsField/coAuthorsField';
import { PeerReviewerField } from './PeerReviewerField/peerReviewerField';
import { getSchemaForm } from '../../utils';
import { ValidationMode } from '../../enums/ValidationMode';
import { MetadataFormFieldGroup } from './MetadataFormFieldGroup/metadataFormFieldGroup';
import { AudienceDetailsGroup } from './AudienceDetailsGroup/audienceDetailsGroup';
import { FormUpdater } from '../../types/Field';
import { FeaturedInsightField } from './FeaturedInsightField/featuredInsightField';

const FieldComponents = {
  ['Item.audienceDetails']: AudienceDetailsGroup,
  [DataType.group]: MetadataFormFieldGroup,
  [FieldType.reference]: ReferenceField,
  [DataType.longString]: TextAreaField,
  [DataType.number]: NumberField,
  [DataType.string]: TextField,
  [DataType.boolean]: BooleanField,
  [DataType.date]: DateField,
  [DataType.enum]: EnumField,
  ['Item.seriesBy']: ConferenceInformationField,
  [FieldType.custom + '_' + DataType.itemRelationship]: RelationshipField,
  [FieldType.custom + '_' + DataType.dataSourceProjects]: DataSourceField,
  [FieldType.custom + '_' + DataType.label]: LabelField,
  ['Item.configureWorkflowType']: ConfigureWorkflowField,
  ['Item.complimentaryResearch']: ComplimentaryResearchField,
  ['Item.additionalMandatoryReviewers']: AdditionalMandatoryReviewerField,
  ['Item.peerReviewer']: PeerReviewerField,
  ['DemandQuestion.delegate']: DelegateField,
  ['Item.coAuthors']: CoAuthorsField,
  ['PriorityJourneyStep.featuredInsights']: FeaturedInsightField
};

export interface FieldProps {
  schema: Schema;
  schemas?: Schema[];
  field: SchemaField;
  data: Partial<Metadata>;
  impliedDataProps?: any;
  disabled?: boolean;
  updater: FormUpdater;
  shouldValidate?: ValidationMode;
  children?: React.ReactElement<Omit<FieldProps, 'children'>>;
  custom?: React.ReactElement<Omit<FieldProps, 'children'>>;
  hideIcon?: boolean;
  componentRight?: React.ReactNode | (() => React.ReactNode);
  isEditMode?: boolean;
  isModal?: boolean;
  pushResponse?: any;
  userSettings?: any;
}

export const Field = ({ children, ...props }: FieldProps) => {
  const { field, schema, schemas, data, shouldValidate } = props;
  const { fieldType, dataType } = field;
  const treeSetting = React.useMemo(() => {
    const schemaForm = getSchemaForm(schema, data, true);
    const ts = TreeFieldUtils.getTreeSetting(schemaForm, field, schemas);
    if (ts?.tree && field.parentField != 'backtag' && ts.settings?.length > 1) {
      for (let i = ts.settings.length - 1; i >= 0; i--) {
        const s = ts.settings[i];
        const schemaField = findOne(schemaForm.props.fields, {
          key: s.field?.key
        });
        if (!schemaField || schemaField.showAt == FieldShowValue.AlwaysHide) {
          ts.settings.pop();
        } else {
          break;
        }
      }
    }
    return ts;
  }, [schema, field, schemas]);
  const fieldDataType = fieldType + '_' + dataType;
  const F = treeSetting
    ? TreeField
    : FieldComponents[field.key] ||
      FieldComponents[dataType] ||
      FieldComponents[fieldType] ||
      (FieldComponents[fieldDataType] as any);
  if (!F) {
    console.error(
      `Invalid field type fieldType:${fieldType}, dataType:${dataType}, fieldDataType: ${fieldDataType}`
    );
    return null;
  }

  const p = { ...props, shouldValidate, ...(treeSetting || {}) };
  if (children) {
    p.custom = React.cloneElement(children, p);
    p.disabled = false;
  }

  if ((field as any)?.isAdditionalInfoGroup) {
    return <F {...p} />;
  }

  return (
    <AccessControl resource={{ ...field, dataSchema: schema }} data={data}>
      <F {...p} />
    </AccessControl>
  );
};
